/**
 * Company eSocial Tab Component
 * Displays eSocial config and manages eSocial events for a company
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Grid,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Settings as SettingsIcon,
  Cloud as CloudIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  ESocialEvent,
  ESocialEventFormData,
  ESocialEventFilters,
  ESocialDashboard,
  ESocialEventType,
  CreateESocialEventRequest,
  UpdateESocialEventRequest,
} from '../../../../types/esocial';
import {
  DocumentType,
  ESocialSendMode,
  DOCUMENT_TYPE_LABELS,
  ESOCIAL_SEND_MODE_LABELS,
} from '../../../../types/company';
import { esocialService } from '../../../../services/esocialService';
import apiClient from '../../../../services/apiClient';
import { employeeService } from '../../../../services/employeeService';
import {
  ESocialStatusBar,
  ESocialFilters,
  ESocialEventsTable,
  ESocialEventFormModal,
  ESocialEventDetailsModal,
} from '../esocial';
import { ActionButton, ProfileSection } from '../../../../components/common';
import { useAcao } from '../../../../authorization/useVeredito';

interface ESocialConfig {
  documentType?: string;
  documentNumber?: string;
  ecacExpirationDate?: string;
  esocialSendMode?: string;
  conditionStartDate?: string;
  procuracaoConfirmada?: boolean;
  procuracaoConfirmadaEm?: string;
}

interface CompanyESocialTabProps {
  companyId: string;
  esocialConfig?: ESocialConfig;
}

interface InfoRowProps {
  label: string;
  value: React.ReactNode;
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value }) => (
  <Box sx={{ display: 'flex', py: 0.5 }}>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ minWidth: 180, fontWeight: 500 }}
    >
      {label}:
    </Typography>
    <Typography variant="body2">{value || '-'}</Typography>
  </Box>
);

const CompanyESocialTab: React.FC<CompanyESocialTabProps> = ({
  companyId,
  esocialConfig: esocialConfigProp,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  /**
   * O eSocial mora dentro da ficha da empresa, que é núcleo e abre com `company.read` — a
   * seção inteira aparecia para os cinco papéis semeados, e nenhum deles tem permissão de
   * eSocial. Ela continua à vista, dizendo por quê, em vez de pedir dados que voltam 403.
   */
  const leitura = useAcao('esocial.events.read', 'Eventos eSocial', { modulo: 'esocial' });
  const [esocialConfig, setEsocialConfig] = useState(esocialConfigProp);

  // Sync with prop changes
  useEffect(() => { setEsocialConfig(esocialConfigProp); }, [esocialConfigProp]);

  // State
  const [events, setEvents] = useState<ESocialEvent[]>([]);
  const [dashboard, setDashboard] = useState<ESocialDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [employees, setEmployees] = useState<Array<{ id: number; fullName: string }>>([]);

  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);

  // Filters
  const [filters, setFilters] = useState<ESocialEventFilters>({});

  // Modal state
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<ESocialEvent | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);

  // Confirmation dialogs
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [retryDialogOpen, setRetryDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [s2240DialogOpen, setS2240DialogOpen] = useState(false);
  const [s2240Loading, setS2240Loading] = useState(false);
  const [s2220DialogOpen, setS2220DialogOpen] = useState(false);
  const [s2220Loading, setS2220Loading] = useState(false);
  const [eventToAction, setEventToAction] = useState<ESocialEvent | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Load dashboard and events
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashboardData, eventsData] = await Promise.all([
        esocialService.getDashboard(companyId),
        esocialService.getEvents(companyId, {
          page,
          limit,
          filters,
        }),
      ]);

      setDashboard(dashboardData);
      setEvents(eventsData.events);
      setTotal(eventsData.total);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados do eSocial');
      enqueueSnackbar('Erro ao carregar dados do eSocial', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [companyId, page, limit, filters, enqueueSnackbar]);

  // Load employees for the form
  const loadEmployees = async () => {
    try {
      const data = await employeeService.getEmployeesByCompany(Number(companyId));
      setEmployees(data.map((emp) => ({ id: emp.id, fullName: emp.name })));
    } catch (err) {
      console.error('Error loading employees:', err);
      setEmployees([]);
    }
  };

  useEffect(() => {
    if (leitura.disabled) return;
    loadData();
    loadEmployees();
  }, [loadData, leitura.disabled]);

  // Handlers
  const handleFilterChange = (newFilters: ESocialEventFilters) => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({});
    setPage(1);
  };

  const handleAddEvent = () => {
    setSelectedEvent(null);
    setModalError(null);
    setFormModalOpen(true);
  };

  const handleViewEvent = (event: ESocialEvent) => {
    setSelectedEvent(event);
    setDetailsModalOpen(true);
  };

  const handleEditEvent = (event: ESocialEvent) => {
    setSelectedEvent(event);
    setModalError(null);
    setFormModalOpen(true);
  };

  // A ficha refeita pelo servidor pode vir sem as relações (funcionário, autor) já carregadas
  const handleEventRefreshed = (refreshed: ESocialEvent) => {
    setSelectedEvent((prev) => (prev ? { ...prev, ...refreshed } : refreshed));
    loadData();
  };

  const handleFormSubmit = async (data: ESocialEventFormData, metadata?: Record<string, unknown> | null) => {
    setModalError(null);
    try {
      const payload: CreateESocialEventRequest | UpdateESocialEventRequest = {
        eventType: data.eventType as ESocialEventType,
        eventDate: data.eventDate,
        employeeId: data.employeeId ? parseInt(data.employeeId) : undefined,
        dueDate: data.dueDate ?? undefined,
        metadata: metadata ?? undefined,
        notes: data.notes ?? undefined,
      };

      if (selectedEvent) {
        await esocialService.updateEvent(
          companyId,
          selectedEvent.id.toString(),
          payload
        );
        enqueueSnackbar('Evento atualizado com sucesso', { variant: 'success' });
      } else {
        await esocialService.createEvent(companyId, payload as CreateESocialEventRequest);
        enqueueSnackbar('Evento criado com sucesso', { variant: 'success' });
      }
      loadData();
    } catch (err: unknown) {
      setModalError(err instanceof Error ? err.message : 'Erro ao salvar evento');
      throw err;
    }
  };

  // Submit event
  const handleSubmitClick = (event: ESocialEvent) => {
    setEventToAction(event);
    setSubmitDialogOpen(true);
  };

  const handleSubmitConfirm = async () => {
    if (!eventToAction) return;

    setActionLoading(true);
    try {
      // Only submit (DRAFT→PENDING) if not already PENDING
      if (eventToAction.eventStatus !== 'PENDING') {
        await esocialService.submitEvent(companyId, eventToAction.id.toString());
      }
      // Trigger actual transmission (PENDING→SUBMITTED)
      const result = await esocialService.transmitEvent(companyId, eventToAction.id.toString());
      if (result.success) {
        enqueueSnackbar(`Evento transmitido com sucesso. Protocolo: ${result.protocoloEnvio ?? '-'}`, { variant: 'success' });
      } else {
        enqueueSnackbar(`Evento enfileirado mas transmissao falhou: ${result.error ?? 'erro desconhecido'}`, { variant: 'warning' });
      }
      loadData();
    } catch (err: unknown) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao transmitir evento', { variant: 'error' });
    } finally {
      setActionLoading(false);
      setSubmitDialogOpen(false);
      setEventToAction(null);
    }
  };

  // Retry failed event
  const handleRetryClick = (event: ESocialEvent) => {
    setEventToAction(event);
    setRetryDialogOpen(true);
  };

  const handleRetryConfirm = async () => {
    if (!eventToAction) return;

    setActionLoading(true);
    try {
      await esocialService.retryEvent(companyId, eventToAction.id.toString());
      enqueueSnackbar('Evento reenviado para fila de transmissao', { variant: 'success' });
      loadData();
    } catch (err: unknown) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao reenviar evento', { variant: 'error' });
    } finally {
      setActionLoading(false);
      setRetryDialogOpen(false);
      setEventToAction(null);
    }
  };

  // Cancel event
  const handleCancelClick = (event: ESocialEvent) => {
    setEventToAction(event);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!eventToAction) return;

    setActionLoading(true);
    try {
      await esocialService.cancelEvent(companyId, eventToAction.id.toString());
      enqueueSnackbar('Evento cancelado com sucesso', { variant: 'success' });
      loadData();
    } catch (err: unknown) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao cancelar evento', { variant: 'error' });
    } finally {
      setActionLoading(false);
      setCancelDialogOpen(false);
      setEventToAction(null);
    }
  };

  // Delete event
  const handleDeleteClick = (event: ESocialEvent) => {
    setEventToAction(event);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!eventToAction) return;

    setActionLoading(true);
    try {
      await esocialService.deleteEvent(companyId, eventToAction.id.toString());
      enqueueSnackbar('Evento excluído com sucesso', { variant: 'success' });
      loadData();
    } catch (err: unknown) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao excluir evento', { variant: 'error' });
    } finally {
      setActionLoading(false);
      setDeleteDialogOpen(false);
      setEventToAction(null);
    }
  };

  // Generate S-2240 batch
  const handleGenerateS2240Batch = () => {
    setS2240DialogOpen(true);
  };

  const handleS2240Confirm = async () => {
    setS2240Loading(true);
    try {
      // Fetch all active employees of this company
      // The backend handles filtering employees without risks
      const employees = await employeeService.getEmployeesByCompany(Number(companyId));
      const employeeIds = (employees ?? [])
        .filter(e => e.status === 'ATIVO')
        .map(e => Number(e.id));

      if (employeeIds.length === 0) {
        enqueueSnackbar('Nenhum funcionário ativo encontrado nesta empresa', { variant: 'warning' });
        return;
      }

      const result = await esocialService.generateS2240Batch(companyId, employeeIds);
      enqueueSnackbar(
        `S-2240 em lote: ${result.created} criados, ${result.drafts} rascunhos, ${result.skipped} ignorados`,
        { variant: result.created > 0 ? 'success' : 'info' }
      );
      loadData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao gerar S-2240 em lote';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setS2240Loading(false);
      setS2240DialogOpen(false);
    }
  };

  // Generate S-2220 batch
  const handleGenerateS2220Batch = () => {
    setS2220DialogOpen(true);
  };

  const handleS2220Confirm = async () => {
    setS2220Loading(true);
    try {
      const employees = await employeeService.getEmployeesByCompany(Number(companyId));
      const employeeIds = (employees ?? [])
        .filter(e => e.status === 'ATIVO')
        .map(e => Number(e.id));

      if (employeeIds.length === 0) {
        enqueueSnackbar('Nenhum funcionário ativo encontrado nesta empresa', { variant: 'warning' });
        return;
      }

      const result = await esocialService.generateS2220Batch(companyId, employeeIds);
      enqueueSnackbar(
        `S-2220 em lote: ${result.created} criados, ${result.drafts} rascunhos, ${result.skipped} ignorados`,
        { variant: result.created > 0 ? 'success' : 'info' }
      );
      loadData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao gerar S-2220 em lote';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setS2220Loading(false);
      setS2220DialogOpen(false);
    }
  };

  if (leitura.disabled) {
    return (
      <ProfileSection title="Eventos eSocial" icon={<CloudIcon />}>
        <Alert severity="info">{leitura.tooltip}</Alert>
      </ProfileSection>
    );
  }

  return (
    <Box>
      {/* eSocial Config Section */}
      {esocialConfig && (
        <ProfileSection title="Configurações eSocial" icon={<SettingsIcon />}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/companies/${companyId}/edit`)}
            >
              Editar configurações
            </Button>
          </Box>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <InfoRow
                label="Tipo de Documento"
                value={
                  esocialConfig.documentType
                    ? DOCUMENT_TYPE_LABELS[esocialConfig.documentType as DocumentType]
                    : '-'
                }
              />
              <InfoRow label="Número do Documento" value={esocialConfig.documentNumber} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <InfoRow
                label="Validade e-CAC"
                value={
                  esocialConfig.ecacExpirationDate
                    ? new Date(esocialConfig.ecacExpirationDate).toLocaleDateString('pt-BR')
                    : '-'
                }
              />
              <InfoRow
                label="Modo de Envio"
                value={
                  esocialConfig.esocialSendMode
                    ? ESOCIAL_SEND_MODE_LABELS[esocialConfig.esocialSendMode as ESocialSendMode]
                    : '-'
                }
              />
              <InfoRow
                label="Início das Condições"
                value={
                  esocialConfig.conditionStartDate
                    ? new Date(esocialConfig.conditionStartDate).toLocaleDateString('pt-BR')
                    : '-'
                }
              />
            </Grid>
          </Grid>

          {/* Procuracao e-CAC */}
          <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Procuração Eletrônica (e-CAC)
            </Typography>
            {esocialConfig?.procuracaoConfirmada ? (
              <Alert severity="success" sx={{ mb: 1 }}>
                Procuração confirmada em{' '}
                {esocialConfig.procuracaoConfirmadaEm
                  ? new Date(esocialConfig.procuracaoConfirmadaEm).toLocaleDateString('pt-BR')
                  : '-'}
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mb: 1 }}>
                Procuração pendente — o cliente precisa configurar no e-CAC
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <ActionButton
                acao="company.update"
                rotulo={esocialConfig?.procuracaoConfirmada ? 'Revogar procuração' : 'Confirmar procuração'}
                modulo="esocial"
                size="small"
                variant={esocialConfig?.procuracaoConfirmada ? 'outlined' : 'contained'}
                color={esocialConfig?.procuracaoConfirmada ? 'error' : 'primary'}
                onClick={async () => {
                  const newState = !esocialConfig?.procuracaoConfirmada;
                  try {
                    await apiClient.patch(`/companies/${companyId}/procuracao`, { confirmed: newState });
                    setEsocialConfig(prev => prev ? {
                      ...prev,
                      procuracaoConfirmada: newState,
                      procuracaoConfirmadaEm: newState ? new Date().toISOString() : undefined,
                    } : prev);
                    enqueueSnackbar(newState ? 'Procuração confirmada' : 'Procuração revogada', { variant: 'success' });
                    loadData();
                  } catch (err: unknown) {
                    const message = err instanceof Error ? err.message : 'Erro ao atualizar procuração';
                    enqueueSnackbar(message, { variant: 'error' });
                  }
                }}
              >
                {esocialConfig?.procuracaoConfirmada ? 'Revogar' : 'Confirmar Procuração'}
              </ActionButton>
              <Button
                size="small"
                variant="text"
                href="https://cav.receita.fazenda.gov.br/"
                target="_blank"
              >
                Acessar e-CAC
              </Button>
            </Box>
          </Box>
        </ProfileSection>
      )}

      {/* Events Section */}
      <ProfileSection
        title="Eventos eSocial"
        icon={<CloudIcon />}
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <ActionButton
              acao="esocial.events.create"
              rotulo="Gerar S-2240 em Lote"
              modulo="esocial"
              size="small"
              variant="outlined"
              onClick={handleGenerateS2240Batch}
            >
              Gerar S-2240 em Lote
            </ActionButton>
            <ActionButton
              acao="esocial.events.create"
              rotulo="Gerar S-2220 em Lote"
              modulo="esocial"
              size="small"
              variant="outlined"
              onClick={handleGenerateS2220Batch}
            >
              Gerar S-2220 em Lote
            </ActionButton>
            <ActionButton
              acao="esocial.events.create"
              rotulo="Novo Evento"
              modulo="esocial"
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddEvent}
            >
              Novo Evento
            </ActionButton>
          </Box>
        }
        noPadding
      >
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ m: 2.5 }}>
            {error}
          </Alert>
        )}

        {/* Status Bar */}
        <Box sx={{ px: 2.5, py: 1.5 }}>
          <ESocialStatusBar dashboard={dashboard} loading={loading} />
        </Box>

        {/* Filters */}
        <Box sx={{ px: 2.5, mb: 2 }}>
          <ESocialFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            loading={loading}
            total={total}
          />
        </Box>

        {/* Events Table */}
        <Box sx={{ px: 2.5, pb: 2.5 }}>
          <ESocialEventsTable
            events={events}
            loading={loading}
            page={page}
            limit={limit}
            total={total}
            onPageChange={setPage}
            onLimitChange={setLimit}
            onView={handleViewEvent}
            onEdit={handleEditEvent}
            onDelete={handleDeleteClick}
            onSubmit={handleSubmitClick}
            onRetry={handleRetryClick}
            onCancel={handleCancelClick}
          />
        </Box>
      </ProfileSection>

      {/* Form Modal */}
      <ESocialEventFormModal
        open={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        onSubmit={handleFormSubmit}
        event={selectedEvent ?? undefined}
        employees={employees}
        error={modalError}
        companyId={companyId}
        onEventRefreshed={handleEventRefreshed}
      />

      {/* Details Modal */}
      <ESocialEventDetailsModal
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        event={selectedEvent}
        companyId={companyId}
        onEventRefreshed={handleEventRefreshed}
      />

      {/* Submit Confirmation Dialog */}
      <Dialog open={submitDialogOpen} onClose={() => setSubmitDialogOpen(false)}>
        <DialogTitle>Confirmar Transmissão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja transmitir este evento para o eSocial?
            Esta ação enviará o evento para processamento pelo governo.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubmitDialogOpen(false)} disabled={actionLoading}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmitConfirm}
            color="primary"
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? 'Transmitindo...' : 'Transmitir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Retry Confirmation Dialog */}
      <Dialog open={retryDialogOpen} onClose={() => setRetryDialogOpen(false)}>
        <DialogTitle>Reenviar Evento</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deseja reenviar este evento para a fila de transmissao?
            O evento sera reprocessado e transmitido novamente.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRetryDialogOpen(false)} disabled={actionLoading}>
            Cancelar
          </Button>
          <Button
            onClick={handleRetryConfirm}
            color="warning"
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? 'Reenviando...' : 'Reenviar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Confirmar Cancelamento</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja cancelar este evento?
            O evento não será mais processado.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={actionLoading}>
            Voltar
          </Button>
          <Button
            onClick={handleCancelConfirm}
            color="warning"
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? 'Cancelando...' : 'Cancelar Evento'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir este evento?
            Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={actionLoading}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* S-2240 Batch Generation Dialog */}
      <Dialog open={s2240DialogOpen} onClose={() => setS2240DialogOpen(false)}>
        <DialogTitle>Gerar S-2240 em Lote</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Esta ação irá gerar eventos S-2240 (Condições Ambientais do Trabalho)
            para os funcionários desta empresa que possuem aplicações de riscos cadastradas.
            Funcionários já com eventos S-2240 ativos serão ignorados.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setS2240DialogOpen(false)} disabled={s2240Loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleS2240Confirm}
            color="primary"
            variant="contained"
            disabled={s2240Loading}
          >
            {s2240Loading ? 'Gerando...' : 'Gerar Eventos'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* S-2220 batch confirmation dialog */}
      <Dialog open={s2220DialogOpen} onClose={() => setS2220DialogOpen(false)}>
        <DialogTitle>Gerar S-2220 em Lote</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Esta ação irá gerar eventos S-2220 (ASO - Monitoramento da Saúde) a partir dos exames
            ocupacionais (ASOs) dos funcionários desta empresa. Exames complementares e consultas
            clínicas não geram S-2220, e ASOs já com evento ativo serão ignorados.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setS2220DialogOpen(false)} disabled={s2220Loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleS2220Confirm}
            color="primary"
            variant="contained"
            disabled={s2220Loading}
          >
            {s2220Loading ? 'Gerando...' : 'Gerar Eventos'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyESocialTab;
